import { useEffect, useMemo, useState } from "react";
import { Loader, Stack, Table, Text } from "@mantine/core";
import { get, ref } from "firebase/database";
import { Order, OrderItem } from "../../models/models";
import { db } from "../../firebase";
import { FIREBASE_DB_ROOT } from "../../App";
import {
  fetchOrdersForPeriod,
  PeriodPreset,
  shouldCountOrder,
} from "./statsShared";
import { StatsPeriodPicker } from "./StatsPeriodPicker";

interface ArticleRow {
  name: string;
  sold: number;
  sumPrice: number;
}

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const getOrderItems = (order: Order | Record<string, unknown>): OrderItem[] => {
  const artikli = (order as { Artikli?: unknown }).Artikli;

  if (!artikli) return [];

  if (Array.isArray(artikli)) {
    return artikli as OrderItem[];
  }

  if (isObject(artikli)) {
    return Object.values(artikli) as OrderItem[];
  }

  return [];
};

const getAllArticleNamesFromSnapshot = (
  snapshotValue: unknown,
): Set<string> => {
  const names = new Set<string>();

  if (!isObject(snapshotValue)) {
    return names;
  }

  const years = Object.values(snapshotValue);

  years.forEach((monthsNode) => {
    if (!isObject(monthsNode)) return;

    Object.values(monthsNode).forEach((daysNode) => {
      if (!isObject(daysNode)) return;

      Object.values(daysNode).forEach((ordersNode) => {
        if (!isObject(ordersNode)) return;

        Object.values(ordersNode).forEach((rawOrder) => {
          if (!isObject(rawOrder)) return;

          const items = getOrderItems(rawOrder);
          items.forEach((item) => {
            const name = item?.Ime?.trim();
            if (name) {
              names.add(name);
            }
          });
        });
      });
    });
  });

  return names;
};

export const SoldArticlesStat = () => {
  const [period, setPeriod] = useState<PeriodPreset>("today");
  const [customDate, setCustomDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [articleRows, setArticleRows] = useState<ArticleRow[]>([]);
  const [articleCatalog, setArticleCatalog] = useState<Set<string>>(new Set());

  useEffect(() => {
    let mounted = true;

    const loadCatalog = async () => {
      setCatalogLoading(true);

      try {
        const snapshot = await get(ref(db, `${FIREBASE_DB_ROOT}/Narudžbe`));
        if (!mounted) return;

        const names = getAllArticleNamesFromSnapshot(snapshot.val());
        setArticleCatalog(names);
      } catch (catalogError) {
        if (!mounted) return;
        console.error("Error loading article catalog", catalogError);
      } finally {
        if (mounted) {
          setCatalogLoading(false);
        }
      }
    };

    loadCatalog();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const orders = await fetchOrdersForPeriod(period, customDate);
        if (!mounted) return;

        const counts = new Map<string, { sold: number; sumPrice: number }>();

        orders.filter(shouldCountOrder).forEach((order) => {
          const items = getOrderItems(order);

          items.forEach((item) => {
            const name = item?.Ime?.trim();
            if (!name) return;

            const current = counts.get(name) ?? { sold: 0, sumPrice: 0 };
            counts.set(name, {
              sold: current.sold + 1,
              sumPrice: current.sumPrice + (Number(item?.Cijena) || 0),
            });
          });
        });

        const allNames = new Set<string>([
          ...Array.from(articleCatalog),
          ...Array.from(counts.keys()),
        ]);

        const rows: ArticleRow[] = Array.from(allNames).map((name) => ({
          name,
          sold: counts.get(name)?.sold ?? 0,
          sumPrice: counts.get(name)?.sumPrice ?? 0,
        }));

        rows.sort((a, b) => b.sold - a.sold || a.name.localeCompare(b.name));
        setArticleRows(rows);
      } catch (statsError) {
        if (!mounted) return;
        console.error("Error loading sold articles stats", statsError);
        setError("Greška kod učitavanja statistike.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [period, customDate, articleCatalog]);

  const totalSold = useMemo(
    () => articleRows.reduce((sum, row) => sum + row.sold, 0),
    [articleRows],
  );

  const totalSumPrice = useMemo(
    () => articleRows.reduce((sum, row) => sum + row.sumPrice, 0),
    [articleRows],
  );

  return (
    <Stack gap="md">
      <StatsPeriodPicker
        period={period}
        customDate={customDate}
        onPeriodChange={setPeriod}
        onCustomDateChange={setCustomDate}
      />

      <Text size="sm" color="dimmed">
        Ukupno prodanih artikala: <strong>{totalSold}</strong>
      </Text>

      {(loading || catalogLoading) && <Loader variant="dots" />}

      {!loading && !catalogLoading && error && <Text color="red">{error}</Text>}

      {!loading && !catalogLoading && !error && articleRows.length === 0 && (
        <Text color="dimmed">Nema podataka za odabrani period.</Text>
      )}

      {!loading && !catalogLoading && !error && articleRows.length > 0 && (
        <Table striped withTableBorder withColumnBorders withRowBorders>
          <thead>
            <tr>
              <th
                style={{
                  border: "1px solid var(--mantine-color-gray-4)",
                  padding: "10px 12px",
                }}
              >
                Artikal
              </th>
              <th
                style={{
                  border: "1px solid var(--mantine-color-gray-4)",
                  padding: "10px 12px",
                }}
              >
                Prodano
              </th>
              <th
                style={{
                  border: "1px solid var(--mantine-color-gray-4)",
                  padding: "10px 12px",
                }}
              >
                Suma cijene
              </th>
            </tr>
          </thead>
          <tbody>
            {articleRows.map((row, index) => (
              <tr
                key={row.name}
                style={{
                  backgroundColor:
                    index % 2 === 1
                      ? "var(--mantine-color-gray-1)"
                      : "transparent",
                }}
              >
                <td
                  style={{
                    border: "1px solid var(--mantine-color-gray-4)",
                    padding: "10px 12px",
                  }}
                >
                  {row.name}
                </td>
                <td
                  style={{
                    border: "1px solid var(--mantine-color-gray-4)",
                    padding: "10px 12px",
                  }}
                >
                  {row.sold}
                </td>
                <td
                  style={{
                    border: "1px solid var(--mantine-color-gray-4)",
                    padding: "10px 12px",
                  }}
                >
                  €{row.sumPrice.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th
                style={{
                  border: "1px solid var(--mantine-color-gray-4)",
                  padding: "10px 12px",
                }}
              >
                Ukupno
              </th>
              <th
                style={{
                  border: "1px solid var(--mantine-color-gray-4)",
                  padding: "10px 12px",
                }}
              >
                {totalSold}
              </th>
              <th
                style={{
                  border: "1px solid var(--mantine-color-gray-4)",
                  padding: "10px 12px",
                }}
              >
                €{totalSumPrice.toFixed(2)}
              </th>
            </tr>
          </tfoot>
        </Table>
      )}
    </Stack>
  );
};
